import type { AiEmployeePlanId } from "~/lib/siteglue/ai-employee-plans";
import {
  DEFAULT_ONBOARD_LAUNCH_STATUS,
  normalizeOnboardLaunchStatus,
  type OnboardLaunchStatus,
} from "~/lib/siteglue/onboard-launch-status";

export type SiteGlueOnboardPhase =
  | "preAuth"
  | "auth"
  | "generating"
  | "workspace";

/** Top-level rail items plus expandable children (channels / training / tools). */
export type SiteGlueOnboardNavId =
  | "view_profile"
  | "profile"
  | "activity"
  | "team"
  | "locations"
  | "channels"
  | "channel_live_chat"
  | "channel_phone"
  | "channel_sms"
  | "channel_email"
  | "channel_browser"
  | "channel_video"
  | "channel_welcome_video"
  | "channel_whatsapp"
  | "channel_messenger"
  | "training"
  | "training_company_profile"
  | "training_icp"
  | "training_website"
  | "training_files"
  | "training_text"
  | "training_qa"
  | "training_video"
  | "training_audio"
  | "training_test"
  | "tools"
  | "tools_appointments"
  | "tools_mcp"
  | "tools_integrations"
  | "conversations"
  | "contacts"
  | "deploy"
  | "preview"
  | "test";

export type SiteGlueOnboardSession = {
  phase: SiteGlueOnboardPhase;
  websiteUrl: string;
  planId: Exclude<AiEmployeePlanId, "enterprise">;
  /** True after the user explicitly picks a plan (chip / composer). Default planId alone is not enough. */
  planConfirmed?: boolean;
  companyName?: string;
  projectId?: string;
  hireIntentId?: string;
  agentName?: string;
  activeNav: SiteGlueOnboardNavId;
  launchStatus: OnboardLaunchStatus;
  /**
   * Dashboard "+ Build new agent" — hire should use the active Clerk org
   * (survives refresh after /onboard?new=1 is stripped from the URL).
   */
  preferActiveOrganization?: boolean;
  /** Homepage role template id, or "custom" when the visitor typed a job title. */
  roleId?: string;
  roleLabel?: string;
  /** True only after the visitor approves the website summary. */
  summaryApproved?: boolean;
  /** Selected role channels from the required setup card. */
  setupChannels?: string[];
  agentImageUrl?: string;
  /** Visitor approved the live profile before install. */
  employeeApproved?: boolean;
  /** Durable Agent-Ready scan context (survives Clerk; scan record is source of truth). */
  scanId?: string;
  scanDomain?: string;
  reportVersion?: string;
  selectedFixIds?: string[];
  desiredChannels?: string[];
  /** Pricing configurator services: aro, aeo. Display-only until Stripe SKUs exist. */
  services?: string[];
  wordpressDetected?: boolean;
  proposedTotalCents?: number | null;
  campaignAttribution?: string;
  emailConsent?: boolean;
  updatedAt: number;
};

export const SITEGLUE_ONBOARD_SESSION_KEY = "siteglue-onboard-session-v1";
export const SITEGLUE_ONBOARD_PATH = "/onboard";

const LEGACY_NAV_ALIASES: Partial<Record<string, SiteGlueOnboardNavId>> = {
  channels: "channel_live_chat",
  training: "training_company_profile",
  tools: "tools_appointments",
};

export function normalizeOnboardNavId(
  id: string | undefined | null,
): SiteGlueOnboardNavId {
  if (!id) return "preview";
  if (id in LEGACY_NAV_ALIASES) return LEGACY_NAV_ALIASES[id]!;
  return id as SiteGlueOnboardNavId;
}

export function defaultOnboardSession(
  overrides?: Partial<SiteGlueOnboardSession>,
): SiteGlueOnboardSession {
  const { launchStatus: launchOverride, planConfirmed, ...rest } = overrides ?? {};
  return {
    phase: "preAuth",
    websiteUrl: "",
    planId: "intern",
    activeNav: "preview",
    updatedAt: Date.now(),
    ...rest,
    planConfirmed: Boolean(planConfirmed),
    launchStatus: normalizeOnboardLaunchStatus(
      launchOverride ?? DEFAULT_ONBOARD_LAUNCH_STATUS,
    ),
  };
}

export function readOnboardSession(): SiteGlueOnboardSession | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(SITEGLUE_ONBOARD_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SiteGlueOnboardSession;
    if (!parsed || typeof parsed !== "object") return null;
    const merged = defaultOnboardSession({
      ...parsed,
      updatedAt: typeof parsed.updatedAt === "number" ? parsed.updatedAt : Date.now(),
      launchStatus: normalizeOnboardLaunchStatus(parsed.launchStatus),
    });
    merged.activeNav = normalizeOnboardNavId(merged.activeNav);
    return merged;
  } catch {
    return null;
  }
}

export function writeOnboardSession(session: SiteGlueOnboardSession): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      SITEGLUE_ONBOARD_SESSION_KEY,
      JSON.stringify({
        ...session,
        launchStatus: normalizeOnboardLaunchStatus(session.launchStatus),
        updatedAt: Date.now(),
      }),
    );
  } catch {
    // ignore quota / private mode
  }
}

export function clearOnboardSession(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(SITEGLUE_ONBOARD_SESSION_KEY);
  } catch {
    // ignore
  }
}

export function patchOnboardSession(
  patch: Partial<SiteGlueOnboardSession>,
): SiteGlueOnboardSession {
  const current = readOnboardSession() ?? defaultOnboardSession();
  const next = {
    ...current,
    ...patch,
    launchStatus: normalizeOnboardLaunchStatus(
      patch.launchStatus
        ? { ...current.launchStatus, ...patch.launchStatus }
        : current.launchStatus,
    ),
    updatedAt: Date.now(),
  };
  if (next.activeNav) {
    next.activeNav = normalizeOnboardNavId(next.activeNav);
  }
  writeOnboardSession(next);
  return next;
}

export function patchOnboardLaunchStatus(
  patch: Partial<OnboardLaunchStatus>,
): SiteGlueOnboardSession {
  const current = readOnboardSession() ?? defaultOnboardSession();
  return patchOnboardSession({
    launchStatus: { ...current.launchStatus, ...patch },
  });
}
